import {Subscription} from "rxjs";
import {NgTemplateOutlet} from "@angular/common";
import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Params, Router, RouterLink} from "@angular/router";

import {appRoutes} from "@numizmat/constants";
import {environment} from "@numizmat/environments";
import {MetaTagService} from "@numizmat/services/common";
import {ProfileApiService} from "@numizmat/services/core";
import {OwnProfileResponse} from "@numizmat/services-models/core";
import {CurrentUserStateService} from "@numizmat/services/account";
import {LoaderComponent, PageHeaderComponent, SidebarMenuComponent, SidebarMenuItem} from "@numizmat/components";

import {
  ProfileDashboardComponent
} from "@numizmat/pages/profile-page/components/own-profile/components/profile-dashboard/profile-dashboard.component";
import {
  ProfileMessagesComponent
} from "@numizmat/pages/profile-page/components/own-profile/components/profile-messages/profile-messages.component";
import {
  ProfileNotificationsComponent
} from "@numizmat/pages/profile-page/components/own-profile/components/profile-notifications/profile-notifications.component";
import {
  ProfileAdsComponent
} from "@numizmat/pages/profile-page/components/own-profile/components/profile-ads/profile-ads.component";
import {
  ProfileSalesComponent
} from "@numizmat/pages/profile-page/components/own-profile/components/profile-sales/profile-sales.component";
import {
  ProfilePurchasesComponent
} from "@numizmat/pages/profile-page/components/own-profile/components/profile-purchases/profile-purchases.component";
import {
  ProfileUserFeedbacksComponent
} from "@numizmat/pages/profile-page/components/own-profile/components/profile-user-feedbacks/profile-user-feedbacks.component";
import {
  ProfileSettingsComponent
} from "@numizmat/pages/profile-page/components/own-profile/components/profile-settings/profile-settings.component";
import {
  ProfileWalletComponent
} from "@numizmat/pages/profile-page/components/own-profile/components/profile-wallet/profile-wallet.component";
import {
  ProfileAdLikesComponent
} from "@numizmat/pages/profile-page/components/own-profile/components/profile-ad-likes/profile-ad-likes.component";
import {
  ProfileFollowersComponent
} from "@numizmat/pages/profile-page/components/own-profile/components/profile-followers/profile-followers.component";
import {
  ProfileFollowedUsersComponent
} from "@numizmat/pages/profile-page/components/own-profile/components/profile-followed-users/profile-followed-users.component";

@Component({
    selector: 'nz-own-profile',
    imports: [
        LoaderComponent,
        PageHeaderComponent,
        ProfileDashboardComponent,
        ProfileMessagesComponent,
        ProfileNotificationsComponent,
        ProfileAdsComponent,
        ProfileSalesComponent,
        ProfilePurchasesComponent,
        ProfileUserFeedbacksComponent,
        ProfileSettingsComponent,
        SidebarMenuComponent,
        ProfileWalletComponent,
        ProfileAdLikesComponent,
        ProfileFollowersComponent,
        ProfileFollowedUsersComponent
    ],
    templateUrl: './own-profile.component.html',
    styleUrl: './own-profile.component.scss'
})
export class OwnProfileComponent implements OnInit, OnDestroy {
  private router: Router = inject(Router);
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private metaTagService: MetaTagService = inject(MetaTagService);
  private profileApiService: ProfileApiService = inject(ProfileApiService);
  private currentUserStateService: CurrentUserStateService = inject(CurrentUserStateService);

  private subs: Subscription = new Subscription();

  protected loading: boolean = true;
  protected profile!: OwnProfileResponse;
  protected menuItems: SidebarMenuItem[] = [];
  protected selectedMenuItem!: SidebarMenuItem;

  protected readonly appRoutes = appRoutes;

  public ngOnInit(): void {
    this.initializeMenuItems();
    this.createQueryParamsChangeSubscription();
    this.getCurrentUser();
    this.getProfile();
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private getProfile() {
    this.profileApiService.getOwnProfile().subscribe({
      next: (response: OwnProfileResponse) => {
        this.loading = false;
        this.profile = response;
        this.metaTagService.setCanonicalUrl(`${environment.applicationDomain}/${appRoutes.profile}/${this.profile.userId}`);
      }
    });
  }

  private getCurrentUser(): void {
    const subscription: Subscription = this.currentUserStateService.getCurrentUser().subscribe({
      next: currentUser => {
        this.menuItems[1].badge = currentUser.userData?.newMessageCount !== 0
          ? currentUser.userData?.newMessageCount
          : undefined;
        this.menuItems[2].badge = currentUser.userData?.newNotificationCount !== 0
          ? currentUser.userData?.newNotificationCount
          : undefined;
      }
    });
    this.subs.add(subscription);
  }

  private initializeMenuItems(): void {
    const currentRoute = this.router.url.split('?')[0];
    this.menuItems = [
      {
        id: 1,
        title: 'Основни данни',
        routerLink: [currentRoute],
        queryParams: {'menuId': '1'},
        iconClass: 'fa-solid fa-house'
      },
      {
        id: 2,
        title: 'Съобщения',
        routerLink: [currentRoute],
        queryParams: {'menuId': '2'},
        iconClass: 'fa-solid fa-comment'
      },
      {
        id: 3,
        title: 'Известия',
        routerLink: [currentRoute],
        queryParams: {'menuId': '3'},
        iconClass: 'fa-solid fa-bell'
      },
      {
        id: 4,
        title: 'Моите обяви',
        routerLink: [currentRoute],
        queryParams: {'menuId': '4'},
        iconClass: 'fa-solid fa-bullhorn'
      },
      {
        id: 5,
        title: 'Харесани обяви',
        routerLink: [currentRoute],
        queryParams: {'menuId': '5'},
        iconClass: 'fa-solid fa-heart'
      },
      {
        id: 6,
        title: 'Портфейл',
        routerLink: [currentRoute],
        queryParams: {'menuId': '6'},
        iconClass: 'fa-solid fa-wallet'
      },
      {
        id: 7,
        title: 'Моите покупки',
        routerLink: [currentRoute],
        queryParams: {'menuId': '7'},
        iconClass: 'fa-solid fa-cart-shopping'
      },
      {
        id: 8,
        title: 'Моите продажби',
        routerLink: [currentRoute],
        queryParams: {'menuId': '8'},
        iconClass: 'fa-solid fa-money-bill-1-wave'
      },
      {
        id: 9,
        title: 'Получени отзиви',
        routerLink: [currentRoute],
        queryParams: {'menuId': '9'},
        iconClass: 'fa-solid fa-star'
      },
      {
        id: 10,
        title: 'Последвани потребители',
        routerLink: [currentRoute],
        queryParams: {'menuId': '10'},
        iconClass: 'fa-solid fa-users'
      },
      {
        id: 11,
        title: 'Моите последователи',
        routerLink: [currentRoute],
        queryParams: {'menuId': '11'},
        iconClass: 'fa-solid fa-users'
      },
      {
        id: 12,
        title: 'Настройки',
        routerLink: [currentRoute],
        queryParams: {'menuId': '12'},
        iconClass: 'fa-solid fa-gears'
      },
      {
        id: 13,
        title: 'Изход',
        routerLink: [appRoutes.root, appRoutes.account, appRoutes.login],
        iconClass: 'fa-solid fa-arrow-right-from-bracket'
      }
    ];
    this.selectedMenuItem = this.menuItems[0];
  }

  private createQueryParamsChangeSubscription(): void {
    const subscription: Subscription = this.activatedRoute.queryParams.subscribe({
      next: (queryParams: Params) => {
        const menuId: string = queryParams['menuId'];
        const index = this.menuItems.findIndex(x => x.id.toString() === menuId);
        if (index >= 0) {
          this.selectedMenuItem = this.menuItems[index];
        } else {
          this.selectedMenuItem = this.menuItems[0];
        }
        this.setMetaTags();
      }
    });
    this.subs.add(subscription);
  }

  private setMetaTags(): void {
    let title = 'Профил | Collectoria';
    let description = 'Профил | Collectoria';
    if (!!this.selectedMenuItem) {
      title = `Профил | ${this.selectedMenuItem.title} | Collectoria`;
      description = `Профил | ${this.selectedMenuItem.title} | Collectoria`;
    }
    this.metaTagService.setTitle(title);
    this.metaTagService.setDescription(description);
  }
}
